<template>
  <div class="late-reasons-page">
    <b-card class="late-hero mb-2">
      <div class="late-hero__inner">
        <div class="late-hero__left">
          <div class="late-hero__icon">
            <feather-icon
              icon="ClockIcon"
              size="22"
            />
          </div>
          <div class="late-hero__copy">
            <h2 class="late-hero__title mb-25">
              Late Reasons
            </h2>
            <p class="late-hero__subtitle mb-0">
              Track engineers late reasons, related requests, and statuses. Use search and Excel export to work faster.
            </p>
          </div>
        </div>

        <div class="late-hero__right">
          <div class="late-hero__hint">
            <feather-icon
              icon="InfoIcon"
              size="16"
              class="mr-50"
            />
            <span>Tip: Press Enter in search to apply.</span>
          </div>
        </div>
      </div>
    </b-card>

    <b-row class="mb-2">
      <b-col
        cols="12"
        md="4"
        class="mb-1"
      >
        <b-card class="kpi-card kpi-card--primary">
          <div class="kpi-card__row">
            <div class="kpi-card__icon">
              <feather-icon
                icon="HashIcon"
                size="18"
              />
            </div>
            <div class="kpi-card__meta">
              <div class="kpi-card__label">
                Total Records
              </div>
              <div class="kpi-card__value">
                {{ totalCount }}
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        md="4"
        class="mb-1"
      >
        <b-card class="kpi-card kpi-card--success">
          <div class="kpi-card__row">
            <div class="kpi-card__icon">
              <feather-icon
                icon="CopyIcon"
                size="18"
              />
            </div>
            <div class="kpi-card__meta">
              <div class="kpi-card__label">
                Pages
              </div>
              <div class="kpi-card__value">
                {{ totalPages }}
              </div>
            </div>
          </div>
        </b-card>
      </b-col>

      <b-col
        cols="12"
        md="4"
        class="mb-1"
      >
        <b-card class="kpi-card kpi-card--info">
          <div class="kpi-card__row">
            <div class="kpi-card__icon">
              <feather-icon
                icon="NavigationIcon"
                size="18"
              />
            </div>
            <div class="kpi-card__meta">
              <div class="kpi-card__label">
                Current Page
              </div>
              <div class="kpi-card__value">
                {{ page }}
              </div>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <b-card
      class="late-table-card"
      no-body
    >
      <div class="late-table-card__head">
        <div class="late-table-card__head-row">
          <div>
            <div class="late-table-card__title">
              <feather-icon
                icon="ListIcon"
                size="16"
                class="mr-50"
              />
              Records
            </div>
            <div class="late-table-card__sub text-muted">
              Each record is shown as a compact card with request and certificate details.
            </div>
          </div>

          <div class="late-toolbar">
            <b-input-group class="late-search">
              <b-input-group-prepend is-text>
                <feather-icon
                  icon="SearchIcon"
                  size="14"
                />
              </b-input-group-prepend>

              <b-form-input
                v-model="searchText"
                placeholder="Search (engineer, request code, reason...)"
                @keydown.enter.prevent="applySearch"
              />

              <b-input-group-append>
                <b-button
                  variant="primary"
                  class="late-search__btn"
                  @click="applySearch"
                >
                  Search
                </b-button>
                <b-button
                  v-if="searchText"
                  variant="outline-secondary"
                  class="late-search__clear"
                  @click="clearSearch"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                  />
                </b-button>
              </b-input-group-append>
            </b-input-group>

            <MakeLateReasonsExcel :skip-page="skipPageForExcel" />
          </div>
        </div>
      </div>

      <div class="late-table-card__body">
        <div
          v-if="isLoading"
          class="late-loading"
        >
          <b-spinner
            variant="primary"
            type="grow"
            label="Loading..."
          />
          <div class="text-muted mt-50">
            Loading late reasons...
          </div>
        </div>

        <div
          v-else-if="!reasons.length"
          class="late-empty"
        >
          <feather-icon
            icon="InboxIcon"
            size="20"
            class="mr-50"
          />
          <span>No late reasons found.</span>
        </div>

        <b-row
          v-else
          class="late-grid"
        >
          <b-col
            v-for="item in reasons"
            :key="item.id"
            cols="12"
            md="6"
            xl="4"
            class="mb-1"
          >
            <b-card class="late-card">
              <div class="late-card__top">
                <div class="late-card__who">
                  <div class="late-card__avatar">
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                    />
                  </div>
                  <div class="late-card__who-meta">
                    <div class="late-card__name">
                      {{ getField(item, 'user.name') }}
                    </div>
                    <div class="late-card__email text-muted">
                      <feather-icon
                        icon="MailIcon"
                        size="13"
                        class="mr-25"
                      />
                      <span class="late-card__email-text">{{ getField(item, 'user.email') }}</span>
                    </div>
                  </div>
                </div>

                <b-badge
                  v-b-tooltip.hover
                  :title="item.status || 'Unknown'"
                  :variant="statusVariant(item.status)"
                  class="late-card__status"
                >
                  {{ truncateWords(item.status || 'Unknown', 2) }}
                </b-badge>
              </div>

              <div class="late-card__reason">
                <div class="late-card__label">
                  <feather-icon
                    icon="MessageSquareIcon"
                    size="14"
                    class="mr-50"
                  />
                  Reason
                </div>
                <div class="late-card__reason-text">
                  {{ item.reason || '—' }}
                </div>
              </div>

              <div class="late-card__meta">
                <div class="late-meta-row">
                  <feather-icon
                    icon="HashIcon"
                    size="14"
                    class="mr-50 text-primary"
                  />
                  <span class="late-meta-row__k">Request Code:</span>
                  <span class="late-meta-row__v">{{ getField(item, 'request.request_code') }}</span>
                </div>
                <div class="late-meta-row">
                  <feather-icon
                    icon="CalendarIcon"
                    size="14"
                    class="mr-50 text-success"
                  />
                  <span class="late-meta-row__k">Source Date:</span>
                  <span class="late-meta-row__v">{{ getField(item, 'request.source_date') }}</span>
                </div>
                <div class="late-meta-row">
                  <feather-icon
                    icon="FileTextIcon"
                    size="14"
                    class="mr-50 text-warning"
                  />
                  <span class="late-meta-row__k">Certificate (EN):</span>
                  <span class="late-meta-row__v">{{ getField(item, 'request.certificate_name.en') }}</span>
                </div>
                <div class="late-meta-row">
                  <feather-icon
                    icon="GlobeIcon"
                    size="14"
                    class="mr-50 text-info"
                  />
                  <span class="late-meta-row__k">Certificate (AR):</span>
                  <span class="late-meta-row__v">{{ getField(item, 'request.certificate_name.ar') }}</span>
                </div>
              </div>

            </b-card>
          </b-col>
        </b-row>

        <div
          v-if="totalPages > 1"
          class="late-pagination"
        >
          <div class="late-pagination__left text-muted">
            <span v-if="totalCount !== null">{{ totalCount }} total records</span>
            <span v-if="perPage"> · {{ perPage }} per page</span>
          </div>
          <b-pagination
            v-model="page"
            :per-page="perPage"
            :total-rows="totalCount"
            first-number
            last-number
            align="right"
            class="mb-0"
            @input="onPageChange"
          />
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import decryptData from '@/store/auth/decryptData'
import buildParams from '@/components/general-table/specific-utils/buildParams'
import MakeLateReasonsExcel from '@/components/late-reasons/MakeLateReasonsExcel.vue'

export default {
  components: {
    MakeLateReasonsExcel,
  },
  data() {
    return {
      isLoading: false,
      reasons: [],
      page: 1,
      perPage: 20,
      totalCount: 0,
      totalPages: 1,
      searchText: '',
    }
  },
  computed: {
    skipPageForExcel() {
      return buildParams(this, this.page).skip || 0
    },
  },
  mounted() {
    this.searchText = this.$route.query.search || ''
    this.fetchReasons()
  },

  methods: {
    truncateWords(text, maxWords = 5) {
      const value = String(text || '').trim()
      if (!value) return '—'
      const words = value.split(/\s+/).filter(Boolean)
      if (words.length <= maxWords) return value
      return `${words.slice(0, maxWords).join(' ')}...`
    },

    getField(obj, path, fallback = '—') {
      if (!obj || !path) return fallback
      let cur = obj
      const parts = String(path).split('.')
      // eslint-disable-next-line no-restricted-syntax
      for (const key of parts) {
        if (cur && Object.prototype.hasOwnProperty.call(cur, key)) {
          cur = cur[key]
        } else {
          return fallback
        }
      }
      return (cur === null || cur === undefined || cur === '') ? fallback : cur
    },

    onPageChange() {
      this.fetchReasons()
    },

    applySearch() {
      this.page = 1
      this.$router.push({
        query: {
          ...this.$route.query,
          ...(this.searchText ? { search: this.searchText } : {}),
          ...(this.searchText ? {} : { search: undefined }),
        },
      }).catch(() => {})

      this.fetchReasons()
    },

    clearSearch() {
      this.searchText = ''
      this.page = 1
      const query = { ...this.$route.query }
      delete query.search
      this.$router.push({ query }).catch(() => {})
      this.fetchReasons()
    },

    statusVariant(status) {
      const s = (status || '').toLowerCase()
      if (s.includes('terminated')) return 'danger'
      if (s.includes('processing')) return 'warning'
      if (s.includes('audit')) return 'info'
      if (s.includes('recommend')) return 'primary'
      return 'secondary'
    },

    async fetchReasons() {
      this.isLoading = true
      try {
        // API uses `skip` pagination (like GeneralTable)
        const params = buildParams(this, this.page)

        const response = await axios.get('late-reasons', { params })

        if (response.status === 200) {
          const responseData = decryptData(response.data.payload)
          const data = responseData?.data

          let reasons = []
          if (Array.isArray(data)) {
            reasons = data
          } else if (Array.isArray(data?.reasons)) {
            reasons = data.reasons
          } else if (data && typeof data === 'object') {
            const firstKey = Object.keys(data)[0]
            if (Array.isArray(data[firstKey])) reasons = data[firstKey]
          }

          this.reasons = reasons || []
          this.totalCount = data?.count ?? this.reasons.length ?? 0
          this.totalPages = data?.total_pages ?? 1

          if (this.totalPages > 0 && this.totalCount > 0) {
            this.perPage = Math.max(1, Math.ceil(this.totalCount / this.totalPages))
          }
        }
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>

<style lang="scss" src="@/assets/scss/pages/late-reasons/late-reasons.scss"></style>
